import { useMemo } from 'react';

import { useSubscriptionsConstruction } from '@adsk/alloy-provider';

import { SUBSCRIPTION_STATUS, SUBSCRIPTION_TYPES } from '../helpers/const';
import { SubscriptionConstruction } from '../helpers/types';
import { isPaidSubscription, shouldShowExpiredMessage } from '../helpers/utils';

const useUserSubscriptions = () => {
  const {
    data: userSubscriptionsConstructionData,
    error: userSubscriptionsConstructionError,
  } = useSubscriptionsConstruction();

  const userTrialSubscriptions = useMemo(() => {
    if (userSubscriptionsConstructionError) return null;
    if (!userSubscriptionsConstructionData) return null;

    return userSubscriptionsConstructionData.filter(
      (subscription: SubscriptionConstruction) =>
        subscription.type === SUBSCRIPTION_TYPES.TRIAL &&
        !isPaidSubscription(subscription, userSubscriptionsConstructionData)
    );
  }, [userSubscriptionsConstructionData, userSubscriptionsConstructionError]);

  const showBanner = useMemo(() => {
    const hasActivePaidSubscription = userSubscriptionsConstructionData?.some(
      (subscription) =>
        isPaidSubscription(subscription, userSubscriptionsConstructionData)
    );

    if (userSubscriptionsConstructionError) return null;
    if (hasActivePaidSubscription) return null;
    if (!userTrialSubscriptions || userTrialSubscriptions.length === 0)
      return null;

    const noActiveTrials = userTrialSubscriptions?.every(
      (subscription: SubscriptionConstruction) =>
        subscription.status === SUBSCRIPTION_STATUS.EXPIRED
    );

    if (noActiveTrials) {
      return shouldShowExpiredMessage(userTrialSubscriptions);
    }

    return true;
  }, [userSubscriptionsConstructionError, userTrialSubscriptions]);

  return {
    userTrialSubscriptions,
    userSubscriptionsError: userSubscriptionsConstructionError,
    showBanner,
  };
};

export default useUserSubscriptions;
